import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

import { ActivityButton } from "../Common";
import {
  AssignedSubjectOption,
  TeacherSubjectAssignment,
} from "./TeacherSubjectAssignment";
import { SelectListDto } from "../../models";
import {
  useGetTeacherAdminUserQuery,
  useUpdateTeacherAdminUserMutation,
} from "../../store/api/teacherAdminApi";
import { TableSpinner } from "../Table";

type TProps = {
  show: boolean;
  onCloseClick: () => void;
  /** When set, loads/saves subjects for this teacher via API */
  teacherId?: string | null;
  teacherName?: string;
  /** Local mode (create teacher): controlled subjects without API */
  value?: AssignedSubjectOption[];
  onChange?: (subjects: AssignedSubjectOption[]) => void;
};

const toOptions = (
  subjects: SelectListDto[],
  langKey: "en" | "ar",
): AssignedSubjectOption[] =>
  (subjects || []).map((item) => ({
    value: item.id,
    label: langKey === "ar" ? item.value.ar : item.value.en,
  }));

export const TeacherSubjectsModal: React.FC<TProps> = ({
  show,
  onCloseClick,
  teacherId,
  teacherName,
  value,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";
  const isPersistMode = !!teacherId;

  const [selectedSubjects, setSelectedSubjects] = useState<
    AssignedSubjectOption[]
  >([]);

  const {
    data: teacher,
    isLoading: loadingTeacher,
    isFetching: fetchingTeacher,
  } = useGetTeacherAdminUserQuery(teacherId as string, {
    skip: !show || !teacherId,
  });

  const [updateTeacherAdminUser, { isLoading: saving }] =
    useUpdateTeacherAdminUserMutation();

  useEffect(() => {
    if (!show) return;
    if (isPersistMode) {
      if (teacher) {
        setSelectedSubjects(toOptions(teacher.subjects || [], langKey));
      }
      return;
    }
    setSelectedSubjects(value || []);
  }, [show, isPersistMode, teacher, value, langKey]);

  const displayName =
    teacherName ||
    (teacher
      ? teacher.name?.[langKey] || teacher.name?.en
      : "") ||
    "";

  const onClose = () => {
    onCloseClick();
  };

  const onSave = async () => {
    if (!selectedSubjects.length) {
      toast.error(t("chooseAtLeast1Subject"), { autoClose: 3000 });
      return;
    }

    if (!isPersistMode) {
      onChange?.(selectedSubjects);
      onClose();
      return;
    }

    if (!teacher || !teacherId) return;

    try {
      await updateTeacherAdminUser({
        id: teacherId,
        data: {
          name: teacher.name,
          gender: teacher.gender,
          countryCode: teacher.countryCode,
          phoneNumber: teacher.phone,
          password: "",
          subjectId: selectedSubjects.map((s) => s.value),
          secondaryPhoneCountryCode: teacher.secondaryPhoneCountryCode || undefined,
          secondaryPhonePhoneNumber: teacher.secondaryPhonePhoneNumber || undefined,
        },
      }).unwrap();
      onChange?.(selectedSubjects);
      toast.success(t("savedSuccessfully"), { autoClose: 3000 });
      onClose();
    } catch (err) {}
  };

  const loading = isPersistMode && (loadingTeacher || fetchingTeacher);

  return (
    <Modal
      isOpen={show}
      toggle={onClose}
      centered
      size="xl"
      backdrop="static"
    >
      <ModalHeader toggle={onClose}>
        {t("manageSubjects")}
        {displayName ? (
          <span className="text-muted fw-normal ms-2">— {displayName}</span>
        ) : null}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <TableSpinner />
        ) : (
          <TeacherSubjectAssignment
            value={selectedSubjects}
            onChange={setSelectedSubjects}
            disabled={loading || saving}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className="btn btn-link link-success fw-medium"
          onClick={onClose}
        >
          <i className="ri-close-line me-1 align-middle"></i> {t("close")}
        </button>
        <ActivityButton
          title="save"
          inProgressTitle="saving"
          color="success"
          className="btn btn-success"
          type="button"
          onClick={onSave}
          inProgress={saving}
          disabled={loading || saving || !selectedSubjects.length}
        />
      </ModalFooter>
    </Modal>
  );
};
